"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223849" className="flex gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/50 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-sm font-semibold uppercase tracking-tight text-foreground hover:text-primary transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-transparent hover:opacity-70">
          <Menu className="size-5" />
          <span className="sr-only">मेनू टॉगल करें</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[85vw] sm:w-[350px] bg-background border-r border-border p-8">
        <div className="flex flex-col h-full">
          <div className="mb-12">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <img src="/img/gotmeds.io_logo.png" alt="GOTMEDS.IO" className="h-7" />
            </Link>
          </div>
          
          <nav className="flex flex-col gap-6 flex-1">
            {loading ? <div className="flex flex-col gap-6">
                {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/50 animate-pulse rounded-sm" />)}
              </div> : menuItems.map((item, i) => <div key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block font-default text-lg font-bold uppercase tracking-tight text-foreground hover:text-primary transition-colors duration-300" onClick={() => setIsOpen(false)}>
                    {item.label}
                  </Link>
                </div>)}
          </nav>

          <div className="mt-auto pt-8 border-t border-border">
            <Link to="/PatientSupportPage" className="inline-flex items-center justify-center w-full rounded-lg border border-secondary text-foreground font-default font-bold uppercase tracking-tight text-xs h-12 hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 shadow-sm" onClick={() => setIsOpen(false)}>
              Schedule Delivery
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-8 h-20 md:h-24 flex items-center justify-between">
        
        {}
        <div className="flex-1 md:hidden flex justify-start">
          <MenuProvider menu_id="16">
            <MobileMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex-1 flex justify-start">
          <Link to="/" className="inline-block transition-opacity hover:opacity-80">
            <img src="https://decent-seal.10web.cloud/wp-content/uploads/2026/04/2d32af_5823d5057c70488e80da6a3dd73fec7emv2.png.avif" alt="GOTMEDS.IO" className="h-7 md:h-8" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-auto justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex-1 flex justify-end">
          <Link to="/PatientSupportPage" className="hidden sm:inline-flex items-center justify-center rounded-lg border border-secondary bg-secondary/10 text-foreground font-default font-bold uppercase tracking-tight text-xs px-6 h-11 hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 shadow-sm hover:shadow-md">
            Schedule Delivery
          </Link>
        </div>

      </div>
    </section>;
}